import { PageContainer } from '../layout/PageContainer';
import { Link } from 'react-router-dom';
import { Button } from '../ui';

export function AboutPage() {
  return (
    <PageContainer>
      <div className="max-w-4xl mx-auto px-4 py-12">
        {/* Hero */}
        <div className="text-center mb-16">
          <h1 className="text-5xl font-bold text-white mb-4">
            About <span className="text-purple-400">After5PM</span>
          </h1>
          <p className="text-xl text-slate-400 max-w-2xl mx-auto">
            Classic arcade games, reimagined for the modern web. Play for free,
            compete on leaderboards, relive the golden age of gaming.
          </p>
        </div>

        {/* Story */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold text-white mb-6">Our Story</h2>
          <div className="prose prose-invert max-w-none">
            <p className="text-slate-300 leading-relaxed mb-4">
              After5PM was born from a simple idea: the best games don&apos;t
              need complex storylines, stunning 3D graphics, or hours of your
              time. Sometimes, you just want to play Snake for 10 minutes after
              work.
            </p>
            <p className="text-slate-300 leading-relaxed mb-4">
              We&apos;ve taken the classic arcade games that defined a
              generation—Falling Blocks, Pill Muncher, Invaders!,
              Blasteroids—and rebuilt them from scratch for modern browsers. No
              downloads, no plugins, no ads interrupting your game. Just pure,
              instant gaming.
            </p>
            <p className="text-slate-300 leading-relaxed">
              Whether you&apos;re killing time during a meeting (we won&apos;t
              tell), taking a break from work, or chasing high scores against
              players worldwide, After5PM is here when you need a quick gaming
              fix.
            </p>
          </div>
        </section>

        {/* Features */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold text-white mb-6">What We Offer</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
              <span className="text-3xl mb-4 block">🎮</span>
              <h3 className="text-lg font-semibold text-white mb-2">
                Classic Games
              </h3>
              <p className="text-slate-400">
                Faithful recreations of arcade classics including Snake, Falling
                Blocks, Pill Muncher, Invaders!, Blasteroids, Wall Breaker,
                Tennis, and more.
              </p>
            </div>

            <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
              <span className="text-3xl mb-4 block">🏆</span>
              <h3 className="text-lg font-semibold text-white mb-2">
                Global Leaderboards
              </h3>
              <p className="text-slate-400">
                Compete with players worldwide. See how your scores stack up and
                climb the rankings.
              </p>
            </div>

            <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
              <span className="text-3xl mb-4 block">⚡</span>
              <h3 className="text-lg font-semibold text-white mb-2">
                Instant Play
              </h3>
              <p className="text-slate-400">
                No downloads, no installations. Games load instantly in your
                browser on any device.
              </p>
            </div>

            <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
              <span className="text-3xl mb-4 block">📱</span>
              <h3 className="text-lg font-semibold text-white mb-2">
                Play Anywhere
              </h3>
              <p className="text-slate-400">
                Desktop, tablet, or phone. Our games adapt to your screen and
                support touch controls.
              </p>
            </div>
          </div>
        </section>

        {/* Free vs Premium */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold text-white mb-6">
            Free vs Premium
          </h2>
          <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-lg font-semibold text-white mb-4">
                  🆓 Free Tier
                </h3>
                <ul className="space-y-2 text-slate-300">
                  <li>✓ Access to 5 classic games</li>
                  <li>✓ Local high scores</li>
                  <li>✓ Basic themes</li>
                  <li>✓ No account required</li>
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white mb-4">
                  ⭐ Premium
                </h3>
                <ul className="space-y-2 text-slate-300">
                  <li>✓ All games unlocked</li>
                  <li>✓ Global leaderboards</li>
                  <li>✓ Custom themes</li>
                  <li>✓ Ad-free experience</li>
                  <li>✓ Early access to new games</li>
                </ul>
              </div>
            </div>
            <div className="mt-6 pt-6 border-t border-slate-700 text-center">
              <Link to="/subscribe">
                <Button variant="primary" size="lg">
                  View Pricing
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* Tech */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold text-white mb-6">Built With</h2>
          <p className="text-slate-300 mb-4">
            After5PM is built with modern web technologies for the best possible
            gaming experience:
          </p>
          <div className="flex flex-wrap gap-3">
            {[
              'React',
              'TypeScript',
              'Phaser 3',
              'Tailwind CSS',
              'AWS Lambda',
              'DynamoDB',
              'CloudFront',
            ].map((tech) => (
              <span
                key={tech}
                className="px-3 py-1 bg-slate-700 rounded-full text-sm text-slate-300"
              >
                {tech}
              </span>
            ))}
          </div>
        </section>

        {/* Contact */}
        <section>
          <h2 className="text-2xl font-bold text-white mb-6">Get in Touch</h2>
          <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
            <p className="text-slate-300 mb-4">
              Have questions, feedback, or just want to say hi? We&apos;d love
              to hear from you.
            </p>
            <div className="space-y-2 text-slate-300">
              <p>
                📧 General:{' '}
                <a
                  href="mailto:hello@after5pm.fun"
                  className="text-purple-400 hover:text-purple-300"
                >
                  hello@after5pm.fun
                </a>
              </p>
              <p>
                🛟 Support:{' '}
                <a
                  href="mailto:support@after5pm.fun"
                  className="text-purple-400 hover:text-purple-300"
                >
                  support@after5pm.fun
                </a>
              </p>
              <p>
                🐛 Bug reports:{' '}
                <a
                  href="mailto:bugs@after5pm.fun"
                  className="text-purple-400 hover:text-purple-300"
                >
                  bugs@after5pm.fun
                </a>
              </p>
            </div>
          </div>
        </section>
      </div>
    </PageContainer>
  );
}
